import { AbstractControl, AbstractControlOptions, FormControl, FormControlState } from "@angular/forms";
import { BehaviorSubject, Observable, Subject } from "rxjs";

import { StringHelper } from "@tylertech/shared-module";

import { FormInitialState, isFormControlState } from "./formStateObject";
import { LgdCustomAsyncValidator, LgdCustomValidator } from "./lgdValidators";
import { ValueOrGetter } from "./model";

export interface LgdFormControlBaseConfig
{
    readonly required: ValueOrGetter<boolean>;

    readonly customValidators?: ReadonlyArray<LgdCustomValidator | LgdCustomAsyncValidator>;
}

export interface LgdFormControlTextConfig extends LgdFormControlBaseConfig
{
    readonly showMaxLengthHint?: ValueOrGetter<boolean>;
    readonly maxLength?: ValueOrGetter<number>;
    readonly minLength?: ValueOrGetter<number>;
}

export interface LgdFormControlTextAreaConfig extends LgdFormControlTextConfig
{
    rows?: ValueOrGetter<number>;
}

export type LgdNumberFormatType = "decimal" | "percent";
export interface LgdFormControlNumberConfig extends LgdFormControlBaseConfig
{
    readonly max?: ValueOrGetter<number>;
    readonly min?: ValueOrGetter<number>;
    readonly precision?: ValueOrGetter<number>;
    readonly scale?: ValueOrGetter<number>;
    readonly noCommas?: ValueOrGetter<boolean>;

    /**
     * Determines how the value should be displayed to the user.
     * A Value of "decimal" will use the `formatNumber` from @angular/common, and "percent" will use `formatPercent` from @angular/common.
     * Defaults to "decimal".
     *
     * @see https://v17.angular.io/api/common/formatNumber
     * @see https://v17.angular.io/api/common/formatPercent
     */
    readonly filter?: LgdNumberFormatType;
}

export interface LgdFormControlDateConfig extends LgdFormControlBaseConfig
{
    readonly max?: ValueOrGetter<Date>;
    readonly min?: ValueOrGetter<Date>;
}

/**
 * TODO - These config options are mostly just placeholders until we have an actual implementation to code against.
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/time
 */
export interface LgdFormControlTimeConfig extends LgdFormControlBaseConfig
{
    /**
     * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/time#time_value_format
     */
    readonly min?: ValueOrGetter<string>;
    /**
     * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/time#time_value_format
     */
    readonly max?: ValueOrGetter<string>;
    /**
     * For time inputs, the value of step is given in seconds, with a scaling factor of 1000 (since the underlying
     * numeric value is in milliseconds). The default value of step is 60, indicating 60 seconds (or 1 minute, or
     * 60,000 milliseconds).
     */
    readonly step?: ValueOrGetter<number>;
}

export interface LgdFormControlFileConfig extends LgdFormControlBaseConfig
{
    readonly maxSize?: ValueOrGetter<number>;
}

export interface LgdFormControlTypeaheadConfig<TOption = unknown, TValue = TOption> extends LgdFormControlBaseConfig
{
    // eslint-disable-next-line @typescript-eslint/method-signature-style
    readonly displayValueFn?: (x: TOption) => string;
    // eslint-disable-next-line @typescript-eslint/method-signature-style
    readonly valueFn?: (x: TOption) => TValue;

    // TODO - need to support free-form entry...
    // ? Should Free-form entry be a different use-case, it's essentially a suggestive text instead of filterable select...
    readonly freeForm?: ValueOrGetter<boolean>;

    autoActiveFirstOption?: boolean;
}

export interface LgdFormControlSelectConfig<TOption = unknown, TValue = unknown> extends LgdFormControlBaseConfig
{
    // eslint-disable-next-line @typescript-eslint/method-signature-style
    readonly displayValueFn?: (x: TOption) => string;
    // eslint-disable-next-line @typescript-eslint/method-signature-style
    readonly valueFn?: (x: TOption) => TValue;
}

export interface LgdFormControlRadioConfig<T> extends LgdFormControlBaseConfig
{
    // eslint-disable-next-line @typescript-eslint/method-signature-style
    readonly displayValueFn?: (x: T) => string;
}

let counter: number = 0;

export type LgdTextFormControl = LgdFormControl<LgdFormControlTextConfig, string>;
export type LgdTextAreaFormControl = LgdFormControl<LgdFormControlTextAreaConfig, string>;

/**
 * @private Use LgdFormControlFactory for create instances of this class.
 *
 * @see LgdFormControlFactory
 */
// TODO - Stop using `any` and switch to `unknown`. Have to fix downstream with change...
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class LgdFormControl<TConfig extends LgdFormControlBaseConfig, TValue = any> extends FormControl<TValue>
{
    private readonly $$_id: string;
    private readonly selectionSubject = new BehaviorSubject<[number, number] | undefined>(undefined);
    private readonly focusSubject = new BehaviorSubject<boolean>(false);

    constructor(formState?: TValue | FormControlState<TValue>, options?: AbstractControlOptions, private _data?: TConfig)
    {
        super(formState, options);
        this.$$_id = `LgdFormControl_${counter++}`;
    }

    /** @private just for debug purposes */
    get id(): string
    {
        return this.$$_id;
    }

    get data(): TConfig { return this._data; }
    set data(v: TConfig) { this._data = v; }

    get onFocus(): Observable<boolean>
    {
        return this.focusSubject.asObservable();
    }

    get selection(): [number, number] { return this.selectionSubject.value; }

    /**
     * Will trigger an event that the lgd-* controls may be listening to that could give the browser's focus to the
     * lgd-* HtmlInputElement.
     */
    focus(): void
    {
        this.focusSubject.next(true);
    }

    /** @private used by Lgd* components when the change is detected on the internal HtmlInputElement  */
    setSelection(start: number, end: number): void
    {
        this.selectionSubject.next([start, end]);
    }

    static isLgdFormControl<T extends LgdFormControlBaseConfig>(x: AbstractControl): x is LgdFormControl<T>
    {
        return x instanceof LgdFormControl;
    }
}

export class LgdTypeaheadFormControl<TOption = unknown, TValue = TOption> extends LgdFormControl<LgdFormControlTypeaheadConfig<TOption, TValue>, TOption>
{
    /**
     * This subject is so we can have a way for the LgdTypeAheadComponent to detect value changes even when `emitEvent`
     * is set to disable the event.
     */
    private readonly onChangeSubject = new Subject<void>();

    /**
     * Fires when the control's value changes but the valueChanges event is purposefully set to avoided with
     * `{ emitEvent: false, emitModelToViewChange: true }`
     */
    get onChange(): Observable<void> { return this.onChangeSubject.asObservable(); }

    override setValue(
        value: TOption,
        options?: {
            onlySelf?: boolean,
            emitEvent?: boolean,
            emitModelToViewChange?: boolean,
            emitViewToModelChange?: boolean,
        }
    ): void
    {
        super.setValue(value, options);

        // We don't want to trigger this event when
        if (options?.emitEvent === false && options?.emitModelToViewChange !== false)
        {
            this.onChangeSubject.next();
        }
    }

}

export function isLgdTypeaheadFormControl<TModel = unknown, TValue = TModel>(x: AbstractControl): x is LgdTypeaheadFormControl<TModel, TValue>
{
    return x instanceof LgdTypeaheadFormControl;
}

/**
 * @private Use LgdFormControlFactory.date for create instances of this class.
 *
 * @see LgdFormControlFactory.date
 */
export class LgdFormDateControl extends LgdFormControl<LgdFormControlDateConfig, string | Date>
{
    constructor(formState?: string | Date | FormControlState<string | Date>, options?: AbstractControlOptions, data?: LgdFormControlDateConfig)
    {
        // TODO - I would really like to be able to call getDate on `formState` but we can't access instance methods before `super()` call.
        // ? Should getDate be made into a static method or module function?
        super(formState, options, data);
    }

    override setValue(
        value: Date | string,
        options?: {
            onlySelf?: boolean,
            emitEvent?: boolean,
            emitModelToViewChange?: boolean,
            emitViewToModelChange?: boolean,
        }
    ): void
    {
        super.setValue(this.getDate(value), options);
    }

    private getDate(formState: FormInitialState<Date | string>): string | Date
    {
        if (!formState || formState instanceof Date)
        {
            return formState as Date;
        }

        if (isFormControlState(formState) && (!formState.value || formState.value instanceof Date))
        {
            return formState.value;
        }

        if (typeof formState === "string") return parseDate(formState);

        if (isFormControlState(formState) && typeof formState.value === "string")
        {
            return parseDate(formState.value);
        }

        throw new Error(`Unexpected formState. was: ${JSON.stringify(formState)}`);

        function parseDate(value: string): Date
        {
            return StringHelper.parseDate(value);
        }
    }

    static isLgdFormDateControl(x: AbstractControl): x is LgdFormDateControl
    {
        return x instanceof LgdFormDateControl;
    }
}
